import { type ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import Svg from "common/core/svg";
import { useMatchScreenClass } from "common/core/responsive";
import {
  SplitLayout,
  SplitLayoutActionButton,
  SplitLayoutHeading,
  SplitLayoutSteps,
  SplitLayoutSubheading,
} from "common/account/guest_signup/shared/split_layout";
import VerifySteps from "assets/images/verify-steps.svg";
import { ProofFooter } from "common/proof_footer";
import { VerifyFaq } from "common/account/guest_signup/lower_content";

import Styles from "./verify.module.scss";
import type {
  SignerLanding_documentBundle_DocumentBundle as DocumentBundle,
  SignerLanding_viewer as Viewer,
} from "../index.query.graphql";
import { LandingWrapper, useHandleReadyToSign } from "./common";

type Props = {
  documentBundle: DocumentBundle;
  viewer: Viewer;
  handleAction: (action: () => void) => void;
};

type InnerProps = Props & {
  renderExpiredOrInactiveContent: () => ReactNode;
};

const verifySteps = [
  {
    heading: (
      <FormattedMessage
        id="1bbaa5e6-02e3-47d6-a8bd-1242d66d0a0c"
        defaultMessage="Tell us who you are"
      />
    ),
    info: (
      <FormattedMessage
        id="829fa1b9-9bda-49b0-94e2-6a0e1bf5ca2c"
        defaultMessage="Fill out some basic information about yourself"
      />
    ),
  },
  {
    heading: (
      <FormattedMessage
        id="68971995-c92d-4b38-94de-3bc41767cadc"
        defaultMessage="Join a video meeting"
      />
    ),
    info: (
      <FormattedMessage
        id="9db33f69-6c3f-499c-8ebe-342e7182c95a"
        defaultMessage="You'll need to bring an ID and a mobile device. You may be asked to take a selfie."
      />
    ),
  },
  {
    heading: (
      <FormattedMessage
        id="a2737520-7c5d-4ad9-944d-38356c310c45"
        defaultMessage="Complete transaction"
      />
    ),
    info: (
      <FormattedMessage
        id="4ddc810a-e209-41c7-8648-128c4f9793ac"
        defaultMessage="The business who requested your verification will get access to an identity report."
      />
    ),
  },
];

function VerifyLanding({
  documentBundle,
  viewer,
  renderExpiredOrInactiveContent,
  handleAction,
}: InnerProps) {
  const handleContinue = useHandleReadyToSign(documentBundle, viewer);
  const isMobile = useMatchScreenClass("xs");
  const expiredOrInactive = renderExpiredOrInactiveContent();

  if (expiredOrInactive) {
    return <>{expiredOrInactive}</>;
  }

  if (!viewer.user?.email) {
    throw Error("Could not find user's email.");
  }

  const actionButton = (
    <SplitLayoutActionButton
      onClick={() => {
        handleAction(handleContinue);
      }}
    >
      <FormattedMessage id="c0df8388-6e0a-44c9-afca-fdcf7474653d" defaultMessage="Verify now" />
    </SplitLayoutActionButton>
  );

  return (
    <div className={Styles.root}>
      <SplitLayout
        heading={
          <SplitLayoutHeading data-growth-id="verify-heading">
            <FormattedMessage
              id="2bf195ce-b6e7-4ac4-937c-6c24cc1554a5"
              defaultMessage="Verify your identity with Proof"
            />
          </SplitLayoutHeading>
        }
        pane1={
          <>
            <SplitLayoutSubheading data-growth-id="verify-subheading">
              <FormattedMessage
                id="d6da2846-5055-40fc-8d38-8da43a17aa24"
                defaultMessage={
                  "Connect with an agent to verify your identity remotely. This will take about 5-10 minutes to complete."
                }
              />
            </SplitLayoutSubheading>

            <SplitLayoutSteps steps={verifySteps} />

            {!isMobile && actionButton}
          </>
        }
        pane2={isMobile ? undefined : <Svg src={VerifySteps} alt="" />}
      />
      <VerifyFaq />
      <ProofFooter />
      {isMobile && <div className={Styles.mobileFooter}>{actionButton}</div>}
    </div>
  );
}

function VerifyLandingWrapper(props: Props) {
  return (
    <LandingWrapper automationPrefix="verify-landing" {...props}>
      {(renderExpiredOrInactiveContent) => (
        <VerifyLanding renderExpiredOrInactiveContent={renderExpiredOrInactiveContent} {...props} />
      )}
    </LandingWrapper>
  );
}

export { VerifyLandingWrapper as VerifyLanding };
